import axios from '@/plugins/axios'
import type { ApiResponse } from '@/types/global'

export type SchoolType = 'kindergarten' | 'preschool'
type ApiSchoolType = 'rawda' | 'preschool'

const toApiSchoolType = (schoolType: SchoolType): ApiSchoolType => {
  return schoolType === 'kindergarten' ? 'rawda' : 'preschool'
}

type SearchCivilRegistryResult = {
  new_registration?: boolean
  existing_student_data?: Record<string, unknown>
  error?: string
}

type StudentPaymentItem = {
  total_amount: number
  due_amount: number
  payment_key: string | false
  name: string
  can_pay: boolean
  is_paid: boolean
  refuse_reason: string | false
  school_period_id: string
}

export type StudentPaymentSearchResult = {
  name?: string
  school_type?: string
  school_id?: string | false
  kindergarten_id?: string | false
  image_base64?: string | false
  stage_id?: string
  state?: string
  can_reject_student?: boolean
  payment?: StudentPaymentItem[]
  error?: string
}

export type FormSchemaField = {
  name: string
  ttype:
    | 'char'
    | 'text'
    | 'date'
    | 'boolean'
    | 'selection'
    | 'many2one'
    | 'many2many'
    | 'binary'
    | 'integer'
    | 'int'
    | 'float'
    | string
  relation: string | null
  required: boolean
  invisible: boolean | string
  standard: boolean
  custom: boolean
  label: string
  onchange: number | string
  readonly: boolean
  view_domain?: boolean | string
  data?: Array<{ id: number; name: string }> | Array<[string, string]>
  value?: unknown
}

export type RemoteFormModel = 'school.student' | 'student.fees.payment' | string
export type RemoteFormInvisibleFields = Record<string, string>
export type RemoteFormInvisibleResult = Record<string, boolean>
export type RemoteFormDomainFields = Record<string, string>
export type RemoteFormDomainResult = Record<string, Array<{ id: number; name: string }>>
export type RemoteFormOnchangeResult = Record<string, unknown>
export type RemoteFormDataValue =
  | string
  | number
  | boolean
  | null
  | Array<string | number | boolean>
export type RemoteFormData = Record<string, RemoteFormDataValue>

type RegistrationFormSchemaResult = {
  form_schema: FormSchemaField[]
  model: string
}

type PaymentRegistrationFormSchemaResult = {
  form_schema: FormSchemaField[]
  bank_informations?: unknown
}

type SubmitRegistrationParams = Record<string, string | number | boolean | Array<string | number>>

const apiPath = (path: string) => (import.meta.env.DEV ? `/api-proxy${path}` : path)

type JsonRpcErrorResponse = {
  error?: {
    message?: string
    data?: {
      message?: string
    }
  }
}

const unwrapJsonRpcResult = <T>(data: ApiResponse<T> & JsonRpcErrorResponse & { result?: T }) => {
  if (data.error) {
    throw new Error(data.error.data?.message || data.error.message || 'تعذر تنفيذ الطلب. حاول مرة أخرى.')
  }

  if (typeof data.result === 'undefined') {
    throw new Error('تعذر تنفيذ الطلب. حاول مرة أخرى.')
  }

  return data.result
}

const normalizeInvisibleExpression = (value: FormSchemaField['invisible']) => {
  if (typeof value === 'boolean') return value ? 'True' : 'False'
  return String(value || 'False')
}

const normalizeDomainExpression = (value: FormSchemaField['view_domain']) => {
  if (value === undefined || value === null || value === false || value === '') return ''
  if (typeof value === 'string') return value.trim()
  if (typeof value === 'number') return String(value)
  return JSON.stringify(value)
}

export const extractInvisibleFieldsFromSchema = (schema: FormSchemaField[]): RemoteFormInvisibleFields => {
  return Object.fromEntries(schema.map((field) => [field.name, normalizeInvisibleExpression(field.invisible)]))
}

export const extractDomainFieldsFromSchema = (schema: FormSchemaField[]): RemoteFormDomainFields => {
  return Object.fromEntries(
    schema
      .map((field) => [field.name, normalizeDomainExpression(field.view_domain)] as const)
      .filter(([, value]) => Boolean(value))
  )
}

export const applyInvisibleFieldsToSchema = (
  schema: FormSchemaField[],
  invisibleFields: RemoteFormInvisibleResult
) => {
  for (const field of schema) {
    if (Object.prototype.hasOwnProperty.call(invisibleFields, field.name)) {
      field.invisible = invisibleFields[field.name]
    }
  }

  return schema
}

export const searchCivilRegistry = async (civilRegistry: string, schoolType: SchoolType) => {
  const apiSchoolType = toApiSchoolType(schoolType)
  const { data } = await axios.post<ApiResponse<SearchCivilRegistryResult>>(
    apiPath('/api/school_management/search_civil_registry'),
    {
      jsonrpc: '2.0',
      params: {
        civil_registry: civilRegistry,
        school_type: apiSchoolType
      }
    }
  )

  return data.result
}

export const fetchRegistrationSchema = async (schoolType: SchoolType) => {
  const endpoint = schoolType === 'kindergarten' ? 'rawda' : 'preschool'
  const { data } = await axios.post<ApiResponse<RegistrationFormSchemaResult>>(
    apiPath(`/api/school_management/${endpoint}`),
    {
      jsonrpc: '2.0',
      params: {}
    }
  )

  return data.result
}

export const submitRegistrationRequest = async (
  schoolType: SchoolType,
  params: SubmitRegistrationParams
) => {
  const endpoint =
    schoolType === 'kindergarten'
      ? '/api/school_management/send_request_rawda'
      : '/api/school_management/send_request_preschool'

  const { data } = await axios.post<
    ApiResponse<{ message?: string; error?: string }> & JsonRpcErrorResponse
  >(
    apiPath(endpoint),
    {
      jsonrpc: '2.0',
      params
    }
  )

  return unwrapJsonRpcResult(data)
}

export const fetchHalaqatRegistrationSchema = async (mosqueId: string | number) => {
  const { data } = await axios.post<ApiResponse<RegistrationFormSchemaResult> & JsonRpcErrorResponse>(
    apiPath(`/api/manarah_school/${mosqueId}/request`),
    {
      jsonrpc: '2.0',
      params: {}
    }
  )

  return unwrapJsonRpcResult(data)
}

export const submitHalaqatRegistrationRequest = async (params: SubmitRegistrationParams) => {
  const { data } = await axios.post<
    ApiResponse<{ message?: string; error?: string }> & JsonRpcErrorResponse
  >(
    apiPath('/api/manarah_school/send_request'),
    {
      jsonrpc: '2.0',
      params
    }
  )

  return unwrapJsonRpcResult(data)
}

export const searchStudentPayment = async (civilRegistry: string, parentMobile: string) => {
  const { data } = await axios.post<ApiResponse<StudentPaymentSearchResult>>(
    apiPath('/api/school_management/search_student_payment'),
    {
      jsonrpc: '2.0',
      params: {
        civil_registry: civilRegistry,
        parent_mobile: parentMobile
      }
    }
  )

  return data.result
}

export const fetchPaymentRegistrationSchema = async (paymentKey: string) => {
  const endpoint = `/api/school_management/register_payment/form/${paymentKey}`
  const { data } = await axios.post<ApiResponse<PaymentRegistrationFormSchemaResult>>(
    apiPath(endpoint),
    {
      jsonrpc: '2.0',
      params: {}
    }
  )

  return data.result
}

export const registerStudentPayment = async (
  paymentKey: string,
  params: Record<string, string | number | boolean | Array<string | number>>
) => {
  const endpoint = `/api/school_management/register_payment/${paymentKey}`
  const { data } = await axios.post<ApiResponse<{ message?: string; error?: string }>>(
    apiPath(endpoint),
    {
      jsonrpc: '2.0',
      params
    }
  )

  return data.result
}

export const runWebInvisible = async (
  model: RemoteFormModel,
  fields: RemoteFormInvisibleFields,
  formData: RemoteFormData
) => {
  const { data } = await axios.post<ApiResponse<RemoteFormInvisibleResult>>(
    apiPath('/web_tools/run_webinvisible'),
    {
      jsonrpc: '2.0',
      method: 'call',
      params: {
        model,
        fields,
        data: formData
      }
    }
  )

  return data.result
}

export const fetchDomainView = async (
  model: RemoteFormModel,
  fields: RemoteFormDomainFields,
  formData: RemoteFormData
) => {
  const { data } = await axios.post<ApiResponse<RemoteFormDomainResult>>(
    apiPath('/web_tools/fetch_domain_view'),
    {
      jsonrpc: '2.0',
      params: {
        model,
        fields,
        data: formData
      }
    }
  )

  return data.result
}

export const runWebOnchange = async (
  model: RemoteFormModel,
  field: string,
  formData: RemoteFormData
) => {
  const { data } = await axios.post<ApiResponse<RemoteFormOnchangeResult>>(
    apiPath('/web_tools/run_web_onchange'),
    {
      jsonrpc: '2.0',
      params: {
        field,
        model,
        data: formData,
        row_data: false,
        global_data: false
      }
    }
  )

  return data.result
}
